/**
 * Inheritance drainer — auto-materialise inherited bearers into usable bridge
 * rows (DEC-465, the OPButtons half of OPBridge Session 148's pairing auto-mint).
 *
 * The problem it closes: a consumer holds a SEPARATE bearer per bridge. When a
 * bridge is reinstalled (config wiped) or a NEW machine joins the mesh, that
 * bridge has no token for this already-paired consumer — historically forcing a
 * re-pair. OPBridge S148 closed the server hole: on peer-join, every bridge that
 * already holds our pairing OFFERS it to the newcomer; the newcomer mints a
 * bearer and REPLIES; the existing bridge (the one we already poll as primary)
 * records an *inheritance* carrying the newcomer's bearer + its stable `node_id`.
 * consumerWs.ts already drains those inheritances into `availableInheritances()`.
 *
 * This module is the "actually use it" half:
 *   1. Adopt each inherited `{ node_id, bearer }` into a real BridgeConfig row so
 *      the connection manager can talk to it with NO re-pair prompt.
 *   2. Resolve `node_id -> bridge_url` via the primary's
 *      `GET /peers/paired-bridges` (joining on `node_id`, NOT the unstable mDNS
 *      `instance_name`) for bridges we've never contacted, then add them.
 *
 * Two cases:
 *   - **Reinstalled peer** (an existing dormant peer row already carries this
 *     node_id but its stored bearer went dead): adopt the fresh bearer in place.
 *     No URL resolve needed. Never touches a CONNECTED primary — see below.
 *   - **New bridge** (no row for this node_id): resolve its URL from the
 *     primary's paired-bridges list, then add a non-primary peer row carrying the
 *     inherited bearer + node_id.
 *
 * Safety invariant (mirrors the OPButtons pairing-persistence contract,
 * opbridge-opbuttons.md): this only ADDS credentials or refreshes a DORMANT peer.
 * It NEVER rewrites the ACTIVE primary's bearer — churning the bearer OPButtons
 * actively dispatches through is the one dangerous move, so a matching primary
 * row is left alone (a genuinely-dead primary bearer still surfaces via the
 * consumerWs revocation path). Documented boundary: reinstalling the PRIMARY
 * itself still falls to that revocation -> re-pair flow, because consumerWs
 * follows only the primary (DEC-063) and can't drain a heal-inheritance while
 * the primary is down. New bridges + reinstalled peers are fully covered.
 *
 * Shape mirrors the other boot-wired opbridge modules (clockPoll / consumerWs):
 * a module-singleton with bare start/stop, idempotent. Wired in App.tsx.
 */

import { createEffect, createRoot, on, untrack } from 'solid-js';
import { availableInheritances } from './consumerWs.ts';
import {
  addBridge,
  bridges,
  getConnection,
  primaryBridge,
  updateBridge,
} from './store.ts';
import { isPeerDeclined } from './storage.ts';
import type { PairingInheritance } from './httpClient.ts';

/** How long to wait before re-attempting a resolve that came back empty (the
 *  newcomer isn't in the primary's paired-bridges list yet — mDNS peer-sync
 *  still propagating, or the primary is momentarily disconnected). One modest
 *  fallback timer; the reactive effect covers the common (already-propagated)
 *  case immediately. */
const RETRY_MS = 8_000;
/** Cap the retry burst after each inheritance-set change so a genuinely
 *  unresolvable inheritance (e.g. the primary advertises it but the bridge
 *  isn't in its paired-bridges list) can't spin an 8s poll forever. ~48s of
 *  coverage absorbs mDNS peer-sync propagation + a momentary primary
 *  disconnect; the counter resets whenever the set changes (a fresh drain
 *  trigger), so a later resync gets a fresh burst. */
const MAX_RETRIES = 6;

interface ResolvedPeer {
  bridge_url: string;
  display_name: string;
  /** The peer's friendly mDNS `instance_name` from paired-bridges. Preferred
   *  for the added row's `bridge_instance_name` over the inheritance's own
   *  field — the auto-mint reply historically carries the minting bridge's
   *  node_id (not the mDNS label) in `bridge_instance_name` (observed live
   *  2026-07-19; design doc flagged the same gap), so the paired-bridges value
   *  is the authoritative friendly name. Optional — the override stub omits it. */
  instance_name?: string;
}

let alive = false;
let disposeRoot: (() => void) | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let retryCount = 0;
/** node_ids with a resolve+add in flight, so a second effect tick (WS push then
 *  poll) can't double-add the same bridge before the first commit lands. */
const inFlight = new Set<string>();
/** Test seam: when set, replaces the live `/peers/paired-bridges` resolve with a
 *  synchronous stub so a PWA-only smoke can drive the add path deterministically
 *  without a live mesh. Production leaves this null. */
let resolveOverride: ((nodeId: string) => ResolvedPeer | null) | null = null;

// ─── public lifecycle ───────────────────────────────────────────────────────

/** Start auto-draining inheritances into bridge rows. Idempotent. Re-runs on
 *  any change to `availableInheritances()`; a fallback timer re-attempts
 *  resolves that weren't yet propagated. */
/** Whether the drain may ADD bridges the operator never paired, as opposed to
 *  only refreshing credentials on rows they already chose. Defaults OFF - see
 *  the REFRESH-ONLY block in `drain()` for why the halves are separable and
 *  why only one of them was rejected. */
let allowAutoAdd = false;

export function startInheritanceDrainer(opts?: { autoAdd?: boolean }): void {
  if (alive) return;
  allowAutoAdd = opts?.autoAdd === true;
  alive = true;
  createRoot((dispose) => {
    disposeRoot = dispose;
    // Fire the drain whenever the inherited set changes. Everything the drain
    // reads from the store is untracked, so its own addBridge/updateBridge
    // writes don't re-trigger the effect (the drain is idempotent regardless,
    // but keeping store reads untracked makes the no-loop guarantee explicit).
    // Reset the retry burst on each set change so a new inheritance gets a fresh
    // window of resolve attempts.
    createEffect(on(availableInheritances, () => { retryCount = 0; void drain(); }));
  });
}

/** Stop and tear down. Used by App onCleanup + tests. */
export function stopInheritanceDrainer(): void {
  alive = false;
  if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
  retryCount = 0;
  if (disposeRoot) { disposeRoot(); disposeRoot = null; }
  inFlight.clear();
}

// ─── drain ──────────────────────────────────────────────────────────────────

async function drain(): Promise<void> {
  const inhs = untrack(() => availableInheritances());
  if (inhs.length === 0) return;
  let needRetry = false;

  for (const inh of inhs) {
    // Pre-v1.15.8 records carry no node_id -> unresolvable (they also predate
    // auto-mint, so nothing to do). A missing bearer is likewise unusable.
    if (!inh.node_id || !inh.bearer) continue;

    const rows = untrack(() => bridges());

    // Already adopted (some row carries this exact bearer) — idempotent no-op.
    if (rows.some((r) => r.bearer === inh.bearer)) continue;

    const match = rows.find((r) => r.nodeId === inh.node_id);
    if (match) {
      // Never churn the ACTIVE primary's bearer (see module invariant). A dead
      // primary bearer is handled by the consumerWs revocation path.
      if (match.is_primary) continue;
      // Reinstalled dormant peer: adopt the fresh, currently-valid bearer.
      updateBridge(match.id, {
        bearer: inh.bearer,
        bridge_instance_name: inh.bridge_instance_name || match.bridge_instance_name,
        scope_bundle_id: inh.scope_bundle_id || match.scope_bundle_id,
        scope_bundle_version: inh.scope_bundle_version || match.scope_bundle_version,
      });
      continue;
    }

    // Explicitly disconnected by the user (Connect page "Disconnect") —
    // never silently re-adopt, no matter how many times the mesh keeps
    // offering it. Only an explicit reconnect (addBridge / a fresh /health
    // backfill) clears this — see storage.ts's declined-peers block.
    if (isPeerDeclined(inh.node_id)) continue;

    // ─── REFRESH-ONLY MODE (default since 2026-09-07) ──────────────────────
    // Everything above this line REFRESHES A CREDENTIAL on a row the operator
    // already chose. Everything below ADDS A BRIDGE they never asked for, and
    // that second half is what was "one step too far" when the whole module
    // was switched off on 2026-08-30.
    //
    // Turning the module back on wholesale would re-add the behaviour that was
    // rejected; leaving it off leaves a bearer that can never recover. So the
    // two halves are separated rather than the module being all-or-nothing.
    //
    // The security reason this split is safe, and it is the SAME boundary the
    // 2026-09-01 audit's F2 finding drew: the refresh branch above takes a
    // BEARER and never a `baseUrl`, for a row that already exists, from an
    // inheritance WE fetched from a bridge we already hold a valid token for.
    // The add branch below takes an ADDRESS resolved from a peer list. F2
    // blocked address-rewriting from an untrusted peer list and must keep
    // doing so — but a credential-only refresh over a channel we initiated is
    // strictly narrower than what F2 was protecting, so it reopens nothing.
    if (!allowAutoAdd) continue;

    // New bridge — resolve its URL then add. Skip if a resolve+add is already
    // in flight for this node, or if a row already claims this instance_name
    // (covers the pre-/health window on a just-added row).
    if (inFlight.has(inh.node_id)) continue;
    if (inh.bridge_instance_name &&
        rows.some((r) => r.bridge_instance_name === inh.bridge_instance_name)) continue;

    inFlight.add(inh.node_id);
    let added = false;
    try {
      added = await resolveAndAdd(inh);
    } finally {
      inFlight.delete(inh.node_id);
    }
    if (!added) needRetry = true;
  }

  if (needRetry) scheduleRetry();
}

/** Resolve the newcomer's `host:port` from the primary's paired-bridges list
 *  (joining on node_id) and add it as a non-primary peer row carrying the
 *  inherited bearer. Returns false when the newcomer isn't resolvable yet (not
 *  in the mesh list, or no reachable primary) so the caller schedules a retry. */
async function resolveAndAdd(inh: PairingInheritance): Promise<boolean> {
  const resolved = await resolvePeerUrl(inh.node_id);
  if (!resolved || !resolved.bridge_url) return false;

  // Re-check idempotency after the await — the set may have changed while the
  // paired-bridges request was in flight.
  const rows = untrack(() => bridges());
  if (rows.some((r) => r.bearer === inh.bearer)) return true;
  if (rows.some((r) => r.nodeId === inh.node_id)) return true;
  if (inh.bridge_instance_name &&
      rows.some((r) => r.bridge_instance_name === inh.bridge_instance_name)) return true;

  addBridge({
    name: resolved.display_name || resolved.instance_name || inh.bridge_instance_name || resolved.bridge_url,
    baseUrl: resolved.bridge_url,
    bearer: inh.bearer,
    // Prefer the friendly mDNS name from paired-bridges; the inheritance's own
    // bridge_instance_name is often just the node_id string (see ResolvedPeer).
    // Either way /health backfills the authoritative value on first success.
    bridge_instance_name: resolved.instance_name || inh.bridge_instance_name,
    nodeId: inh.node_id,
    scope_bundle_id: inh.scope_bundle_id || null,
    scope_bundle_version: inh.scope_bundle_version || null,
    is_primary: false,
  });
  return true;
}

/** Join `node_id -> { bridge_url, display_name }` via the primary's
 *  `GET /peers/paired-bridges`. Returns null when there's no reachable primary,
 *  the request fails, or the newcomer isn't in the list yet. */
async function resolvePeerUrl(nodeId: string): Promise<ResolvedPeer | null> {
  if (resolveOverride) return resolveOverride(nodeId);
  const primary = untrack(() => primaryBridge());
  if (!primary) return null;
  const conn = getConnection(primary.id);
  if (!conn) return null;
  try {
    const resp = await conn.httpClient().pairedBridges();
    const entry = resp.bridges.find((b) => (b.node_id ?? '') === nodeId);
    if (!entry || !entry.bridge_url) return null;
    return {
      bridge_url: entry.bridge_url,
      display_name: entry.display_name,
      instance_name: entry.instance_name,
    };
  } catch {
    return null;
  }
}

function scheduleRetry(): void {
  if (retryTimer || !alive) return;
  if (retryCount >= MAX_RETRIES) return;   // burst exhausted; wait for the next set change
  retryCount += 1;
  retryTimer = setTimeout(() => { retryTimer = null; void drain(); }, RETRY_MS);
}

// ─── DEV test hook (drives the production drain path without a live mesh) ─────

declare global {
  interface Window {
    __opbuttonsInheritanceDrainer?: {
      /** Force a drain pass now (awaited). */
      drainNow(): Promise<void>;
      /** node_ids currently in the registry with NO adopting row yet. */
      pending(): string[];
      /** Swap the paired-bridges resolve for a synchronous stub (null to
       *  restore the live resolve). Lets a PWA-only smoke exercise the
       *  new-bridge add path deterministically. */
      setResolveOverride(fn: ((nodeId: string) => ResolvedPeer | null) | null): void;
      /** Read/DRIVE the auto-add half. Production wires `autoAdd: false`
       *  (App.tsx) so no bridge the operator did not pair ever appears; a
       *  smoke needs to exercise BOTH modes - the refresh path that ships,
       *  and the add path that must stay off. Without this a smoke could only
       *  assert the shipped mode, and "add never happens" would be untestable
       *  for the same reason it is invisible. */
      setAutoAdd(on: boolean): void;
      autoAdd(): boolean;
    };
  }
}

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  window.__opbuttonsInheritanceDrainer = {
    drainNow: () => drain(),
    pending: () => {
      const rows = bridges();
      return availableInheritances()
        .filter((inh) => inh.node_id && inh.bearer && !rows.some((r) => r.bearer === inh.bearer))
        .map((inh) => inh.node_id);
    },
    setResolveOverride: (fn) => { resolveOverride = fn; },
    setAutoAdd: (on) => { allowAutoAdd = on === true; },
    autoAdd: () => allowAutoAdd,
  };
}
